import type { ColumnDef, Row } from "@tanstack/react-table";
import { format } from "date-fns";
import type {
  AgendaItem,
  AgendaModule,
  Approver as ApproverType,
  Document,
  Meeting,
  Team,
  User,
} from "@repo/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/Base/DropDown";
import { Button } from "@repo/ui/components/Base/Button";
import {
  CheckIcon,
  ClockIcon,
  EllipsisIcon,
  MoreHorizontal,
  SquareArrowOutUpRight,
  XIcon,
} from "lucide-react";
import { DataTableColumnHeader } from "@repo/ui/components/Base/DataTableColumnHeader";
import { DocumentSummaryItem } from "../documents/DocumentSummaryItem";
import { RoleProtected } from "../common/RoleProtected";
import { Approver } from "./Approver";
import { AgendaModuleSummaryItem } from "../agendaModules/AgendaModuleSummaryItem";
import { DATE_FORMAT, LABEL_REVIEW_AGENDA_ITEM_BUTTON } from "@/lib/constants";
import { CappedLinkList } from "../common/CappedLinkList";
import { AppUrlPath } from "@/lib/AppUrlPath";
import {
  CacheMap,
  createCreatedAtColumn,
  createCreatedByColumn,
  createDescriptionColumn,
  createExpirationDateColumn,
  createIdColumn,
  createNameColumn,
  createStatusColumn,
  createTagColumn,
  createTeamColumn,
  createUpdatedAtColumn,
  createUpdatedByColumn,
  getUsedByFilterFn,
} from "../dataTable/columns";
import type { TableLayoutState } from "@repo/ui/components/Base/DataTable";
import { useUserStore } from "@/stores/userStore";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useMemo } from "react";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import { capitalize, cn } from "@repo/shared/utils";
import { tenantStore } from "@/stores/tenantStore";
import { useMeetingList } from "@/hooks/queryHooks/meetingHooks";
import {
  agendaItemRepo,
  type AgendaItemDto,
} from "@/repositories/agendaItemRepo";
import { meetingRepo } from "@/repositories/meetingRepo";
import { firstValueFrom, map } from "rxjs";
import { userRepo } from "@/repositories/userRepo";
import { teamRepo } from "@/repositories/teamRepo";
import { agendaModuleRepo } from "@/repositories/agendaModuleRepo";
import { documentRepo } from "@/repositories/documentRepo";

export class ApproverMap extends CacheMap<
  { type: "user"; object: User | null } | { type: "team"; object: Team | null }
> {
  constructor() {
    super((tenantId, key) => {
      const [type, id] = key.split(":");
      if (type === "user") {
        return userRepo
          .get(id)
          .pipe(map((object) => ({ type: "user", object })));
      } else {
        return teamRepo
          .get(tenantId, id)
          .pipe(map((object) => ({ type: "team", object })));
      }
    });
  }

  async getApprover(approver: ApproverType | undefined, tenantId: string) {
    if (!approver || !["user", "team"].includes(approver.approverType ?? "")) {
      return { type: "", object: null };
    }
    return await this.get(
      tenantId,
      `${approver.approverType}:${approver.approverId}`
    );
  }
}

const PendingColumn = ({
  row,
  tenantId,
}: {
  row: Row<AgendaItemDto>;
  tenantId: string;
}) => {
  const { data: meetings } = useMeetingList(tenantId, "org");
  const pending = agendaItemRepo.isPending(row.original, meetings);

  return (
    <div>
      {pending === true ? (
        <Tooltip asChild label="Pending">
          <ClockIcon />
        </Tooltip>
      ) : pending === false ? null : (
        <EllipsisIcon />
      )}
    </div>
  );
};

export const createAgendaItemPendingColumn = (
  tenantId: string
): ColumnDef<AgendaItemDto> => ({
  accessorKey: "pending",
  filterFn: "async",
  meta: {
    label: "Pending",
    filterVariant: "customBoolean",
    customBooleanOptions: {
      trueLabel: "Pending",
      falseLabel: "Not-Pending",
    },
    async asyncFilterFn(row, _columnId, rawFilterValue, _table, rawState) {
      const state = rawState as { meetings?: Promise<Meeting[]> };
      const filterValue = rawFilterValue as boolean;

      // load meetings once, and cross-share promise
      if (!state.meetings) {
        const tenantId = tenantStore.getState().id;
        state.meetings = firstValueFrom(meetingRepo.list(tenantId));
      }

      const meetings = await state.meetings;
      return agendaItemRepo.isPending(row.original, meetings) === filterValue;
    },
  },
  sortingFn: (a, b) => {
    const pendA = agendaItemRepo.isPending(a.original);
    const pendB = agendaItemRepo.isPending(b.original);
    return pendA && !pendB ? -1 : !pendA && pendB ? 1 : 0;
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Pending" />
  ),
  cell: ({ row }) => <PendingColumn row={row} tenantId={tenantId} />,
});

export const AgendaItemRequestedApprovalDateColumn: ColumnDef<AgendaItemDto> = {
  accessorKey: "requestedApprovalDate",
  filterFn: "date",
  meta: {
    label: "Req. Approval Date",
    filterVariant: "date",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Req. Approval Date" />
  ),
  cell: ({ row }) => {
    const requestedApprovalDate = row.getValue("requestedApprovalDate");
    if (!requestedApprovalDate) return null;
    return (
      <div>{format(requestedApprovalDate as number | Date, DATE_FORMAT)}</div>
    );
  },
};

export const AgendaItemDocApprovalTypeColumn: ColumnDef<AgendaItemDto> = {
  accessorKey: "docapprovaltype",
  meta: {
    label: "Approval Type",
    filterVariant: "select",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Approval Type" />
  ),
  cell: ({ row }) => {
    const docapprovaltype = row.getValue("docapprovaltype");
    if (!docapprovaltype) return null;
    return (docapprovaltype as string).toUpperCase();
  },
};

export const createAgendaItemApproverColumn = (
  tenantId: string
): ColumnDef<AgendaItemDto> => ({
  accessorKey: "approver",
  filterFn: "async",
  meta: {
    label: "Approver",
    filterVariant: "approver",
    async asyncFilterFn(row, _columnId, rawFilterValue, _table, rawState) {
      const filterValue = rawFilterValue as
        | { type?: "user" | "team" | "none" | ""; name?: string }
        | undefined;

      // if filter is off, always match
      if (!filterValue || (!filterValue.type && !filterValue.name)) return true;

      // set up state
      const state = rawState as { map: ApproverMap };
      state.map ??= new ApproverMap();

      // load approver object
      const approver = await state.map.getApprover(
        row.original.approver,
        tenantId
      );

      // object has no approver
      if (!approver || !approver.type || !approver.object) {
        return filterValue.type === "none";
      }

      // test approver type
      if (filterValue.type && filterValue.type !== approver.type) return false;

      // test approver name
      const name =
        approver.type === "user"
          ? `${approver.object.firstName} ${approver.object.lastName}`
          : approver.object.name;
      if (
        filterValue.name &&
        !name.toLowerCase().includes(filterValue.name.toLowerCase())
      )
        return false;

      // matches
      return true;
    },
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Approver" />
  ),
  cell: ({ row }) => {
    type Approver = {
      approverId?: string;
      approverType: string;
    };
    const approver = row.getValue("approver") as Approver | null | undefined;
    return (
      <Approver
        approverId={approver?.approverId}
        approverType={approver?.approverType}
        tenantId={tenantId}
      />
    );
  },
});

const hasApprover = (item: AgendaItemDto) =>
  !!item.approver && item.approver.approverType !== "none";

export const AgendaItemReviewStatusColumn: ColumnDef<AgendaItemDto> = {
  id: "reviewStatus",
  // accessorFn required for sorting to work due to behavior when review.status is undefined:
  accessorFn: (row) =>
    hasApprover(row) ? "n/a" : row.review?.status ?? "pending",
  meta: {
    label: "Review Status",
    filterVariant: "selectReviewStatus",
  },
  sortingFn: (a, b) => {
    const statusA = hasApprover(a.original)
      ? a.original.review?.status ?? "pending"
      : "zzzz"; // n/a -> zzzz: move to end
    const statusB = hasApprover(b.original)
      ? b.original.review?.status ?? "pending"
      : "zzzz"; // n/a -> zzzz: move to end

    return statusA < statusB ? -1 : statusA > statusB ? 1 : 0;
  },
  filterFn: (row, _, value) => {
    const status = row.original.review?.status ?? "pending";

    if (!value) return true;
    if (!hasApprover(row.original)) return value === "n/a";

    if (value === "unresolved")
      return status === "pending" || status === "deferred";
    if (value === "resolved")
      return status === "approved" || status === "rejected";

    return value === status;
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Review Status" />
  ),
  cell: ({ row }) => {
    return (
      <div>
        {hasApprover(row.original)
          ? capitalize(row.original.review?.status ?? "Pending")
          : "N/A"}
      </div>
    );
  },
};

export const AgendaItemPriceColumn: ColumnDef<AgendaItemDto> = {
  accessorKey: "price",
  meta: {
    label: "Price This Year",
    filterVariant: "text",
  },
  filterFn: "includesString",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Price This Year" />
  ),
  cell: ({ row }) => {
    const price = row.getValue("price");
    return <div>{price && typeof price === "string" ? price : ""}</div>;
  },
};

export const AgendaItemAnnualPriceColumn: ColumnDef<AgendaItemDto> = {
  accessorKey: "annualPrice",
  meta: {
    label: "Annual Price",
    filterVariant: "text",
  },
  filterFn: "includesString",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Annual Cost" />
  ),
  cell: ({ row }) => {
    const annualPrice = row.getValue("annualPrice");
    return (
      <div>
        {annualPrice && typeof annualPrice === "string" ? annualPrice : ""}
      </div>
    );
  },
};

export const AgendaItemTermColumn: ColumnDef<AgendaItemDto> = {
  accessorKey: "term",
  meta: {
    label: "Term in Months",
    filterVariant: "text",
  },
  filterFn: "includesString",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Term in Months" />
  ),
  cell: ({ row }) => {
    const term = row.getValue("term");
    return <div>{term && typeof term === "string" ? term : ""}</div>;
  },
};

export const GLAccountColumn: ColumnDef<AgendaItemDto> = {
  accessorKey: "glAccount",
  meta: {
    label: "GL Account",
    filterVariant: "text",
  },
  filterFn: "includesString",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="GL Account" />
  ),
  cell: ({ row }) => {
    const glAccount = row.getValue("glAccount");
    return (
      <div>
        {glAccount && typeof glAccount === "string" ? glAccount : "N/A"}
      </div>
    );
  },
};

export const AgendaItemGASB87: ColumnDef<AgendaItemDto> = {
  accessorKey: "lease",
  meta: {
    label: "GASB 87 Leases",
    filterVariant: "customBoolean",
    customBooleanOptions: {
      trueLabel: "Yes",
      falseLabel: "No",
    },
  },
  filterFn: (row, _columnId, filterValue: boolean) => {
    const lease = row.original.lease;
    return !!lease === filterValue;
  },
  sortingFn: (a, b) => {
    const leaseA = a.original.lease;
    const leaseB = b.original.lease;
    return leaseA && !leaseB ? -1 : !leaseA && leaseB ? 1 : 0;
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="GASB 87" />
  ),
  cell: ({ row }) => {
    return row.original.lease ? (
      <CheckIcon className="text-tertiary" />
    ) : (
      <XIcon className="text-destructive" />
    );
  },
};

export const AgendaItemGASB96: ColumnDef<AgendaItemDto> = {
  accessorKey: "software",
  meta: {
    label: "GASB 96 Subscription based IT solutions",
    filterVariant: "customBoolean",
    customBooleanOptions: {
      trueLabel: "Yes",
      falseLabel: "No",
    },
  },
  filterFn: (row, _columnId, filterValue: boolean) => {
    const software = row.original.software;
    return !!software === filterValue;
  },
  sortingFn: (a, b) => {
    const softwareA = a.original.software;
    const softwareB = b.original.software;
    return softwareA && !softwareB ? -1 : !softwareA && softwareB ? 1 : 0;
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="GASB 96" />
  ),
  cell: ({ row }) => {
    return row.original.software ? (
      <CheckIcon className="text-tertiary" />
    ) : (
      <XIcon className="text-destructive" />
    );
  },
};

export const createAgendaItemUsedByColumn = (
  tenantId: string
): ColumnDef<AgendaItemDto> => ({
  id: "usedBy",
  accessorKey: "parentIds",
  filterFn: "async",
  meta: {
    label: "Agenda Sections Used In",
    filterVariant: "usedBy",
    asyncFilterFn: getUsedByFilterFn<AgendaItemDto, AgendaModule>(
      "parentIds",
      agendaModuleRepo,
      tenantId,
      "name"
    ),
  },
  header: ({ column }) => (
    <DataTableColumnHeader
      column={column}
      title="Agenda Sections Used In"
      noSort
    />
  ),
  cell: ({ row }) => {
    const agendaItem = row.original;

    return (
      <CappedLinkList
        ids={agendaItem.parentIds}
        morePath={new AppUrlPath(tenantId).getAgendaItemPath(agendaItem.id)}
      >
        {(agendaModuleId) => (
          <AgendaModuleSummaryItem
            key={agendaModuleId}
            tenantId={tenantId}
            agendaModuleId={agendaModuleId}
          />
        )}
      </CappedLinkList>
    );
  },
});

export const createAgendaItemDocumentsColumn = (
  tenantId: string
): ColumnDef<AgendaItemDto> => ({
  id: "documents",
  accessorKey: "documentIds",
  filterFn: "async",
  meta: {
    label: "Documents",
    filterVariant: "usedBy",
    asyncFilterFn: getUsedByFilterFn<AgendaItem, Document>(
      "documentIds",
      documentRepo,
      tenantId,
      "name"
    ),
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Documents" noSort />
  ),
  cell: ({ row }) => {
    const agendaItem = row.original;

    return (
      <CappedLinkList
        ids={agendaItem.documentIds}
        morePath={new AppUrlPath(tenantId).getAgendaItemPath(agendaItem.id)}
      >
        {(documentId) => (
          <DocumentSummaryItem
            key={documentId}
            tenantId={tenantId}
            documentId={documentId}
          />
        )}
      </CappedLinkList>
    );
  },
});

export const useColumns = ({
  userId,
  tenantId,
  handleQuickEdit,
  handleReoccurring,
  handleArchive,
  handleReview,
  handleView,
}: {
  userId: string;
  tenantId: string;
  handleQuickEdit: (id: string) => void;
  handleReoccurring: (id: string) => void;
  handleArchive: (id: string) => void;
  handleReview: (id: string) => void;
  handleView: (id: string) => void;
}) => {
  handleQuickEdit = useFutureCallback(handleQuickEdit);
  handleReoccurring = useFutureCallback(handleReoccurring);
  handleArchive = useFutureCallback(handleArchive);
  handleReview = useFutureCallback(handleReview);
  handleView = useFutureCallback(handleView);

  const col: ColumnDef<AgendaItemDto>[] = useMemo(() => {
    return [
      {
        id: "actions",
        header: "",
        cell: function Cell({ row }) {
          const { team, user, hasMinRole } = useUserStore((s) => ({
            team: s.activeTeam,
            user: s.user,
            hasMinRole: s.hasMinRole,
          }));
          const isFullOrg = !team || team === "org";

          const reviewAllowed =
            row.original.approver &&
            (hasMinRole("edit") ||
              (user &&
                row.original.approver.approverType === "user" &&
                row.original.approver.approverId === user.id &&
                hasMinRole("view")));

          const agendaItem = row.original;
          return (
            <div className="flex flex-row align-middle gap-1 justify-start">
              <Button
                variant="ghost"
                tooltip={
                  isFullOrg ? "Pick a team first" : "Open this agenda item"
                }
                className={cn(
                  "h-8 w-8 p-0",
                  isFullOrg && "opacity-50 cursor-not-allowed"
                )}
                disabled={isFullOrg}
                onClick={() => handleView(agendaItem.id)}
              >
                <SquareArrowOutUpRight />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <RoleProtected minRole="view">
                    {agendaItem.createdBy === userId ? (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleQuickEdit(agendaItem.id)}
                        >
                          Edit
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <RoleProtected minRole="edit">
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          disabled={isFullOrg}
                          tooltip={isFullOrg ? "Pick a team first" : ""}
                          onClick={() => handleQuickEdit(agendaItem.id)}
                        >
                          Edit
                        </DropdownMenuItem>
                      </RoleProtected>
                    )}
                  </RoleProtected>
                  <RoleProtected minRole="submit">
                    <DropdownMenuItem
                      disabled={isFullOrg}
                      tooltip={
                        isFullOrg
                          ? "Pick a team first"
                          : "Create scheduled copies of an item"
                      }
                      onClick={() => handleReoccurring(agendaItem.id)}
                    >
                      Reoccurring Copies
                    </DropdownMenuItem>
                  </RoleProtected>
                  {reviewAllowed && (
                    <DropdownMenuItem
                      onClick={() => handleReview(agendaItem.id)}
                    >
                      {LABEL_REVIEW_AGENDA_ITEM_BUTTON}
                    </DropdownMenuItem>
                  )}
                  <RoleProtected minRole="edit">
                    <DropdownMenuItem
                      onClick={() => handleArchive(agendaItem.id)}
                    >
                      Archive
                    </DropdownMenuItem>
                  </RoleProtected>
                  <DropdownMenuItem
                    onClick={() => navigator.clipboard.writeText(agendaItem.id)}
                  >
                    Copy agenda item ID
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableHiding: false,
      },
      createIdColumn(),
      createNameColumn(),
      createAgendaItemPendingColumn(tenantId),
      createTagColumn(),
      createDescriptionColumn(),
      createStatusColumn(),
      createCreatedAtColumn(),
      createCreatedByColumn(tenantId),
      createUpdatedAtColumn(),
      createUpdatedByColumn(tenantId),
      createTeamColumn(tenantId),
      AgendaItemRequestedApprovalDateColumn,
      AgendaItemDocApprovalTypeColumn,
      createAgendaItemApproverColumn(tenantId),
      AgendaItemReviewStatusColumn,
      createExpirationDateColumn(),
      AgendaItemPriceColumn,
      AgendaItemAnnualPriceColumn,
      AgendaItemTermColumn,
      GLAccountColumn,
      AgendaItemGASB87,
      AgendaItemGASB96,
      createAgendaItemUsedByColumn(tenantId),
      createAgendaItemDocumentsColumn(tenantId),
    ];
  }, [
    userId,
    tenantId,
    handleQuickEdit,
    handleReoccurring,
    handleArchive,
    handleReview,
    handleView,
  ]);
  return col;
};

export const layoutDefaults: TableLayoutState = {
  columnFilters: [
    { id: "pending", value: true },
    { id: "approver", value: { type: "team" } },
  ],
  columnVisibility: {
    id: false,
    updatedAt: false,
    usedBy: false,
    lease: false,
    software: false,
    price: false,
    annualPrice: false,
    term: false,
  },
};
